import { type FC } from 'react';
import { ClickableIcon, HStack } from '@direct-frontend/components';
import { type OpenApiSchemaDashboardTodolistItem } from '../../../../api/schema';
import { IconSelectCircle } from '../../../../ui/icons/IconSelectCircle';
import { IconSelectCircleHovered } from '../../../../ui/icons/IconSelectCircleHovered';
import styles from './CompletionIcon.module.css';

type TodoCompletionGlyphProps = {
    isCompleted: boolean;
};

function TodoCompletionGlyph({ isCompleted }: TodoCompletionGlyphProps) {
    const iconProps = {
        'aria-hidden': true as const,
        color: (isCompleted ? 'green' : 'gray-disabled') as 'green' | 'gray-disabled',
        role: (isCompleted ? 'main' : 'supplementary') as 'main' | 'supplementary',
        size: '24' as const,
    };

    if (!isCompleted) {
        return (
            <>
                <HStack alignItems="center" justifyContent="center" className={styles.glyphRest}>
                    <IconSelectCircle {...iconProps} />
                </HStack>
                <HStack alignItems="center" justifyContent="center" className={styles.glyphHover}>
                    <IconSelectCircleHovered {...iconProps} />
                </HStack>
            </>
        );
    }

    return (
        <>
            <HStack alignItems="center" justifyContent="center" className={styles.glyphRest}>
                <IconSelectCircleHovered {...iconProps} />
            </HStack>
            <HStack alignItems="center" justifyContent="center" className={styles.glyphHover}>
                <IconSelectCircle {...iconProps} />
            </HStack>
        </>
    );
}

export type DashboardTodoListItemCompletionIconProps = {
    item: OpenApiSchemaDashboardTodolistItem;
    onToggleItemComplete: (item: OpenApiSchemaDashboardTodolistItem) => void;
    isCompleteUpdating: boolean;
    'data-testid'?: string;
};

export const DashboardTodoListItemCompletionIcon: FC<DashboardTodoListItemCompletionIconProps> = ({
    item,
    onToggleItemComplete,
    isCompleteUpdating,
    'data-testid': dataTestId,
}) => {
    const isCompleted = Boolean(item.completed_at);

    const ariaLabel = isCompleted ? 'Отметить пункт как невыполненный' : 'Отметить пункт как выполненный';

    return (
        <ClickableIcon
            aria-label={ariaLabel}
            className={styles.completionClickable}
            clickableAreaSize="20"
            data-testid={dataTestId ? `${dataTestId}.CompletionIconToggle` : undefined}
            isDisabled={isCompleteUpdating}
            role="supplementary"
            onClick={() => onToggleItemComplete(item)}
        >
            <HStack alignItems="center" justifyContent="center" width="max" height="max">
                <TodoCompletionGlyph isCompleted={isCompleted} />
            </HStack>
        </ClickableIcon>
    );
};
